import type { Discipline } from "@/domains/pm/types";
import { groupDisciplinesByCode } from "@/domains/pm/utils/disciplineName";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { DisciplineBadge } from "./DisciplineBadge";

interface DisciplineBadgesProps {
  disciplines: readonly Discipline[];
  size?: "sm" | "md" | "lg";
  variant?: "solid" | "outline";
  /** Pills drawn before the rest collapse into a muted `+N`. */
  max?: number;
}

/**
 * One pill per discipline CODE, not per discipline. Disciplines sharing a
 * code (Heating and Ventilation both read KVJ / HVAC) become a single pill
 * whose tooltip names each of them, rather than two identical pills side by
 * side. Renders a fragment, so the caller keeps its own flex container.
 */
export function DisciplineBadges({ disciplines, size, variant, max }: DisciplineBadgesProps) {
  const { language } = useLanguage();
  const groups = groupDisciplinesByCode(disciplines, language);
  const shown = max === undefined ? groups : groups.slice(0, max);
  const hidden = groups.length - shown.length;
  return (
    <>
      {shown.map(([first, ...rest]) => (
        <DisciplineBadge
          key={first.id}
          discipline={first}
          additional={rest}
          size={size}
          variant={variant}
        />
      ))}
      {hidden > 0 && <span className="text-[10px] text-muted-foreground">+{hidden}</span>}
    </>
  );
}
